import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { axiosClient } from '@/helpers/auth';
import { useGlobalStore } from '@/store/storeGlobal';
import { useNotificationsStore } from '@/store/notificationsStore';
import { useUploadStoreV2 } from '@/store/storeUploadV2.js';

export const useFilePreviewStore = defineStore('filePreviewStore', () => {
  const globalStore = useGlobalStore();
  const notificationsStore = useNotificationsStore();
  const { files } = storeToRefs(useUploadStoreV2());

  const debugPrefix = '[FilePreview]';

  // State
  const preview = ref(null);
  const analyse = ref(null);
  const selectedFileUploadId = ref(null);
  const fileConfig = ref({});
  const selectedFileName = ref('');
  const isSelectedFileTextFile = ref(false);
  const errorReadingFile = ref(false);
  const errorMessage = ref('');
  const selectedSheet = ref(null)

  setDefault();

  function setDefault () {
    fileConfig.value = {
      previewLines: 50,
      analysisLines: 10000,
      searchQuery: null,
      separator: ',',
      escape: '\\',
      quote: '"',
      encoding: 'UTF-8',
      headerRow: 1,
      firstDataRow: 2,
    };
  }

  function setValues (metadata) {
    if (!metadata) {
      setDefault();
      return;
    }
    fileConfig.value = {
      ...fileConfig.value,
      separator: metadata.separator,
      escape: metadata.escape,
      quote: metadata.quote,
      encoding: metadata.encoding,
      headerRow: metadata.headerRow,
      firstDataRow: metadata.firstDataRow,
    };
  }

  function getMetadata () {
    return {
      separator: fileConfig.value.separator,
      escape: fileConfig.value.escape,
      quote: fileConfig.value.quote,
      encoding: fileConfig.value.encoding,
      headerRow: fileConfig.value.headerRow,
      firstDataRow: fileConfig.value.firstDataRow,
    };
  }

  // Methods
  async function getFilePreview (uploadId) {
    const params = {
      uploadId,
      previewLines: fileConfig.value.previewLines,
    };
    const response = await axiosClient.get('v2/preview-file', { params });

    // Sjekk om axios returnerte false (feil)
    if (response === false) {
      errorReadingFile.value = true;
      errorMessage.value = 'En feil oppstod ved lasting av filen';
      return false;
    }

    if (response?.data) {
      preview.value = response.data;
      selectedFileUploadId.value = response.data.uploadId;
      selectedFileName.value = response.data.fileName;
      isSelectedFileTextFile.value = response.data.isTextFile;
      if (selectedFileUploadId.value !== selectedSheet.value) {
        console.log('[getFilePreview] has changed sheet...', response.data.sheets)
        selectedSheet.value = selectedFileUploadId.value;
      }
      setValues(response.data.metadata);
      errorReadingFile.value = response.data.hasError;
      errorMessage.value = response.data.errorMessage;
      globalStore.debug(`${debugPrefix} preview`, preview.value);
      return true;
    }

    // Hvis vi kommer hit, så hadde vi ikke data
    errorReadingFile.value = true;
    errorMessage.value = 'Ingen data mottatt fra server';
    return false;
  }

  async function analyseFile (tableDefinitionId, importDefinitionId) {
    const analysisRequest = {
      uploadId: selectedFileUploadId.value,
      analysisLines: fileConfig.value.analysisLines,
      tableDefinitionId,
      importDefinitionId,
    };
    globalStore.debug(`${debugPrefix} analyseFile`, analysisRequest);

    const response = await axiosClient.get('file-analyse', { params: analysisRequest });
    if (response) {
      analyse.value = response?.data;
      globalStore.debug(`${debugPrefix} analyse`, analyse.value);
      notificationsStore.addSuccess('Filen er analysert!');
      return true;
    }
    return false;
  }

  function clearPreview () {
    preview.value = null;
  }

  function clearAnalyse () {
    analyse.value = null;
  }

  const filteredFiles = computed(() => {
    if (!fileConfig.value.searchQuery) return files.value;
    return files.value?.filter(file =>
      file.fileName?.toLowerCase().includes(fileConfig.value.searchQuery?.toLowerCase()),
    );
  });

  const isFirstDataRowValid = computed(() => {
    return fileConfig.value.firstDataRow > fileConfig.value.headerRow;
  });

  async function addUnlinkedColumns (importDefinitionId) {
    const tableColumnName = '';
    const payload = {
      columns: Object.values(analyse.value.columnsComparison)
        .filter(column => column.mappingStatus === 'unable to map')
        .map(column => ({
          sourceColumnName: column.sourceColumnName,
          tableColumnName,
          ordinal: column.fileColumn?.ordinal,
        })),
    };

    await axiosClient.post(`import-definition/${importDefinitionId}/add-columns`, payload);
  }

  async function addSuggestedColumns (importDefinitionId) {
    const payload = {
      columns: Object.values(analyse.value.columnsComparison)
        .filter(column => column.mappingStatus === 'suggestion')
        .map(column => ({
          sourceColumnName: column.sourceColumnName,
          tableColumnName: column.tableColumnName,
          ordinal: column.fileColumn?.ordinal,
        })),
    };

    await axiosClient.post(`import-definition/${importDefinitionId}/add-columns`, payload);
  }

  return {
    // State
    preview,
    analyse,
    fileConfig,
    selectedSheet,
    // Methods
    getFilePreview,
    getMetadata,
    analyseFile,
    clearPreview,
    clearAnalyse,
    filteredFiles,
    isFirstDataRowValid,
    addUnlinkedColumns,
    addSuggestedColumns,
    selectedFileName,
    isSelectedFileTextFile,
    errorReadingFile,
    errorMessage,

  };
});
